import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { isForbidden, useRecords } from '@/analytics/reportKit'
import { StateView } from '@/card/record/StateView'
import { Icon, type IconName } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import type { Locale } from '@/i18n'
import { ImportPanel } from '@/lists/ImportPanel'
import { TaskFormModal } from '@/calendar/ActivityForms'
import { completeTask, fetchMyTasks } from './api'
import type { Task } from './types'
import '@/styles/work-analytics.css'

/**
 * «Задачи» (Tasks.dc.html; ТЗ §4.2, тикет #79).
 *
 * ## Просрочку считает сервер
 *
 * «Срок прошёл» — вопрос о том, какое сейчас время, и два клиента в разных часовых поясах
 * ответили бы по-разному, глядя на одни и те же строки. Экран показывает флаг `overdue`, а
 * не сравнивает даты; по дням клиент только раскладывает остальное.
 *
 * ## Учётная запись без карточки сотрудника
 *
 * Задачу выполняет сотрудник. У учётной записи без карточки задач нет по устройству, и
 * «задач нет» читалось бы как «все сделаны» — поэтому это «Не настроено» словами.
 *
 * ## Новая задача — с записью или без (#110)
 *
 * «Новая задача» ставит задачу без привязки к записи («отчёт руководителю»), с повтором и
 * напоминанием; из карточки её ставят с записью. Следующий экземпляр повторяющейся задачи
 * появляется после выполнения — его порождает сервер, экран только перечитывает список.
 *
 * ## Чего нет из макета
 *
 * «Выполненные», чек-лист, комментарии и «Поставил»: `GET /tasks/my` отдаёт только
 * открытые задачи без автора и пунктов. Рисовать это значило бы обещать данные, которых нет.
 */

type Segment = 'all' | 'today' | 'overdue' | 'upcoming'

const KIND_ICON: Record<string, IconName> = { call: 'call', email: 'mail', meeting: 'meet', task: 'work' }
const ENTITY_PATH: Record<string, string> = {
  deal: '/deals',
  lead: '/leads',
  counterparty: '/counterparties',
  contact: '/contacts',
  invoice: '/invoices',
}

export function TasksPage() {
  const { t, locale, user } = useApp()
  const queryClient = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)
  const [segment, setSegment] = useState<Segment>('all')
  const [kind, setKind] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const tasks = useQuery({
    queryKey: ['my-tasks'],
    queryFn: ({ signal }) => fetchMyTasks(signal),
    retry: false,
  })

  const complete = useMutation({
    mutationFn: (id: string) => completeTask(id),
    onSuccess: () => {
      setNotice(null)
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] })
    },
    onError: (error: unknown) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  // Просроченные приходят и отдельным списком, и внутри общего — здесь строка одна.
  const all = dedupe([...(tasks.data?.overdue ?? []), ...(tasks.data?.open ?? [])])
  const now = new Date()
  const names = useEntityNames(all)

  const needle = search.trim().toLowerCase()
  const filtered = all
    .filter((task) => !kind || task.kind === kind)
    .filter((task) => !needle || task.subject.toLowerCase().includes(needle))
  const overdue = filtered.filter((task) => task.overdue)
  const rest = filtered.filter((task) => !task.overdue).sort(byWhen)
  const today = rest.filter((task) => dayOffset(whenOf(task), now) === 0)
  const tomorrow = rest.filter((task) => dayOffset(whenOf(task), now) === 1)
  const later = rest.filter((task) => {
    const offset = dayOffset(whenOf(task), now)
    return offset == null || offset > 1 || offset < 0
  })

  const groups: { key: string; label: string; danger?: boolean; items: Task[] }[] = []
  if (segment === 'all' || segment === 'overdue') groups.push({ key: 'od', label: t('tasks.group.overdue', { count: overdue.length }), danger: true, items: overdue })
  if (segment === 'all' || segment === 'today') groups.push({ key: 'today', label: t('tasks.group.today', { count: today.length }), items: today })
  if (segment === 'all' || segment === 'upcoming') {
    groups.push({ key: 'tomorrow', label: t('tasks.group.tomorrow', { count: tomorrow.length }), items: tomorrow })
    groups.push({ key: 'later', label: t('tasks.group.later', { count: later.length }), items: later })
  }
  const shown = groups.filter((group) => group.items.length > 0)
  const visible = shown.flatMap((group) => group.items)
  const selected = visible.find((task) => task.id === selectedId) ?? visible[0] ?? null

  // X — отметить выполненной выбранную задачу; в поле ввода это просто буква.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable === true)) return
      if ((event.key === 'x' || event.key === 'X' || event.key === 'ч') && selected && !complete.isPending) {
        complete.mutate(selected.id)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selected, complete])

  const head = (
    <div className="page-head">
      <h1 className="h1">{t('tasks.title')}</h1>
      <span className="spacer" />
      {/* The task import is gated by sales.activity:create on the server (TaskImportTarget). */}
      {can(user, 'sales.activity', 'create') && tasks.data?.hasEmployeeCard && (
        <ImportPanel target="sales.task" onImported={() => void queryClient.invalidateQueries({ queryKey: ['my-tasks'] })} />
      )}
      {can(user, 'sales.activity', 'create') && tasks.data?.hasEmployeeCard && (
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
          <Icon name="plus" size="sm" />
          {t('tasks.new')}
        </button>
      )}
      {creating && <TaskFormModal onClose={() => setCreating(false)} />}
    </div>
  )

  if (tasks.error) {
    return (
      <div className="stack" style={{ gap: 14 }}>
        {head}
        {isForbidden(tasks.error) ? (
          <StateView kind="forbidden" boxed title={t('tasks.forbidden.title')} description={t('tasks.forbidden.text')} />
        ) : (
          <StateView
            kind="error"
            boxed
            title={t('tasks.error.title')}
            description={tasks.error instanceof ApiError ? tasks.error.message : t('tasks.error.text')}
            actions={
              <button className="btn btn-primary btn-sm" type="button" onClick={() => void tasks.refetch()}>
                <Icon name="refresh" size="sm" />
                {t('state.retry')}
              </button>
            }
            code={tasks.error instanceof ApiError ? String(tasks.error.status) : undefined}
          />
        )}
      </div>
    )
  }

  if (tasks.data && !tasks.data.hasEmployeeCard) {
    return (
      <div className="stack" style={{ gap: 14 }}>
        {head}
        <StateView
          kind="setup"
          boxed
          title={t('tasks.noEmployeeCard')}
          description={t('tasks.noEmployeeCard.text')}
          actions={
            <Link className="btn btn-ghost btn-sm" to="/my-day">
              {t('approvals.toMyDay')}
            </Link>
          }
        />
      </div>
    )
  }

  const count = (items: Task[]) => <span className="muted">{items.length}</span>
  const todayAll = all.filter((task) => !task.overdue && dayOffset(whenOf(task), now) === 0)
  const upcomingAll = all.filter((task) => {
    if (task.overdue) return false
    const offset = dayOffset(whenOf(task), now)
    return offset == null || offset >= 1 || offset < 0
  })

  return (
    <div className="stack" style={{ gap: 14 }}>
      {head}
      <p className="page-lead">{t('tasks.lead')}</p>
      {notice && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span className="fg">{notice}</span>
        </div>
      )}

      <div className="toolbar">
        <div className="seg" role="group" aria-label={t('tasks.segment')}>
          <button type="button" aria-pressed={segment === 'all'} onClick={() => setSegment('all')}>
            {t('tasks.seg.all')} {count(all)}
          </button>
          <button type="button" aria-pressed={segment === 'today'} onClick={() => setSegment('today')}>
            {t('tasks.seg.today')} {count(todayAll)}
          </button>
          <button type="button" aria-pressed={segment === 'overdue'} onClick={() => setSegment('overdue')}>
            {t('tasks.seg.overdue')} {count(all.filter((task) => task.overdue))}
          </button>
          <button type="button" aria-pressed={segment === 'upcoming'} onClick={() => setSegment('upcoming')}>
            {t('tasks.seg.upcoming')} {count(upcomingAll)}
          </button>
        </div>
        <div className="vsep" />
        {(['call', 'meeting', 'email'] as const).map((code) => (
          <button key={code} className="chip" type="button" aria-pressed={kind === code} onClick={() => setKind(kind === code ? null : code)}>
            <Icon name={KIND_ICON[code]!} />
            {t(`tasks.kind.${code}`)} <span className="count">{all.filter((task) => task.kind === code).length}</span>
          </button>
        ))}
        <div className="input-icon wa-search">
          <Icon name="search" />
          <label htmlFor="tq" className="sr-only">{t('tasks.search')}</label>
          <input id="tq" className="input input-sm" placeholder={t('tasks.search')} value={search} onChange={(event) => setSearch(event.target.value)} />
        </div>
      </div>

      {tasks.isLoading ? (
        <div className="panel" aria-busy="true" aria-label={t('tasks.loading')}>
          {Array.from({ length: 8 }, (_, index) => (
            <div key={index} className="list-item">
              <span className="skel" style={{ width: 16, height: 16 }} />
              <span className="skel skel-c" style={{ width: 24, height: 24 }} />
              <div className="stack" style={{ gap: 6, flex: 1 }}>
                <span className="skel" style={{ width: `${44 + ((index * 13) % 26)}%` }} />
                <span className="skel" style={{ width: '30%', height: 8 }} />
              </div>
              <span className="skel" style={{ width: 96 }} />
            </div>
          ))}
        </div>
      ) : shown.length === 0 ? (
        <StateView
          kind="empty"
          boxed
          title={t(all.length === 0 ? 'tasks.empty' : 'tasks.emptyFiltered')}
          description={t(all.length === 0 ? 'tasks.emptyText' : 'tasks.emptyFilteredText')}
          actions={
            all.length > 0 ? (
              <button className="btn btn-secondary btn-sm" type="button" onClick={() => { setSegment('all'); setKind(null); setSearch('') }}>
                {t('tasks.showAll', { count: all.length })}
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="layout-2">
          <section className="panel" aria-label={t('tasks.list')} style={{ overflow: 'hidden' }}>
            {shown.map((group) => (
              <div key={group.key}>
                <div className={group.danger ? 'list-sub is-danger' : 'list-sub'}>
                  {group.danger && <Icon name="clock" size="xs" />}
                  {group.label}
                </div>
                {group.items.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    now={now}
                    locale={locale}
                    selected={selected?.id === task.id}
                    entityName={task.entityId == null ? undefined : names.get(task.entityId)}
                    busy={complete.isPending}
                    onSelect={() => setSelectedId(task.id)}
                    onComplete={() => complete.mutate(task.id)}
                  />
                ))}
              </div>
            ))}
          </section>

          {selected && (
            <TaskDetails
              task={selected}
              now={now}
              entityName={selected.entityId == null ? undefined : names.get(selected.entityId)}
              busy={complete.isPending}
              onComplete={() => complete.mutate(selected.id)}
            />
          )}
        </div>
      )}
    </div>
  )
}

function TaskRow({
  task,
  now,
  locale,
  selected,
  entityName,
  busy,
  onSelect,
  onComplete,
}: {
  task: Task
  now: Date
  locale: Locale
  selected: boolean
  entityName: string | undefined
  busy: boolean
  onSelect: () => void
  onComplete: () => void
}) {
  const { t } = useApp()
  const when = whenOf(task)
  return (
    <div className={selected ? 'list-item is-selected wa-task' : 'list-item wa-task'}>
      <input
        type="checkbox"
        checked={false}
        disabled={busy}
        aria-label={t('myday.tasks.complete', { subject: task.subject })}
        onChange={onComplete}
      />
      <span className="feed-ico" style={{ width: 24, height: 24 }}>
        <Icon name={KIND_ICON[task.kind] ?? 'work'} size="sm" />
      </span>
      <div className="stack" style={{ flex: 1, gap: 0, minWidth: 0 }}>
        <button type="button" className="wa-task-title ellipsis" aria-pressed={selected} onClick={onSelect}>
          {task.subject}
        </button>
        <EntityLink type={task.entityType} id={task.entityId} name={entityName} />
      </div>
      <span className="nowrap wa-when">
        {task.overdue ? (
          // Просрочка словом, а не цветом: цвет не переживает ни печать, ни дальтонизм.
          <span className="flag flag-danger">
            <Icon name="clock" />
            {when ? `${shortWhen(when, now, locale)} · ` : ''}
            {t('tasks.overdueBadge')}
          </span>
        ) : (
          <span className="t-sm secondary">{when ? shortWhen(when, now, locale) : t('myday.noDue')}</span>
        )}
      </span>
    </div>
  )
}

function TaskDetails({
  task,
  now,
  entityName,
  busy,
  onComplete,
}: {
  task: Task
  now: Date
  entityName: string | undefined
  busy: boolean
  onComplete: () => void
}) {
  const { t, locale } = useApp()
  const when = whenOf(task)
  return (
    <aside className="panel" aria-labelledby="h-task">
      <div className="panel-h" style={{ alignItems: 'flex-start', paddingTop: 10, paddingBottom: 10 }}>
        <div className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
          <span className="overline row" style={{ gap: 4 }}>
            <Icon name={KIND_ICON[task.kind] ?? 'work'} size="xs" />
            {t('tasks.overline', { kind: t(`tasks.kind.${task.kind}`) })}
          </span>
          <span className="h2" id="h-task">{task.subject}</span>
        </div>
      </div>
      <div className="panel-b stack" style={{ gap: 14 }}>
        <dl className="kv" style={{ gridTemplateColumns: '96px minmax(0, 1fr)' }}>
          <dt>{t('tasks.due')}</dt>
          <dd>
            {when ? <span className="strong">{longWhen(when, now, locale, t)}</span> : <span className="muted">{t('myday.noDue')}</span>}
            {task.overdue && (
              <>
                {' '}
                <span className="flag flag-danger">{t('tasks.overdueFlag')}</span>
              </>
            )}
          </dd>
          <dt>{t('tasks.status')}</dt>
          <dd>{t(`tasks.status.${task.status}`)}</dd>
          {task.recurrence && (
            <>
              <dt>{t('cal.form.repeat')}</dt>
              <dd>
                {t(`cal.repeat.${task.recurrence}`)}
                {task.recurrenceUntil ? ` · ${t('cal.repeat.until', { date: task.recurrenceUntil })}` : ''}
              </dd>
            </>
          )}
          {task.remindBeforeMinutes != null && (
            <>
              <dt>{t('cal.form.remind')}</dt>
              <dd>{t('cal.remind.before', { minutes: task.remindBeforeMinutes })}</dd>
            </>
          )}
        </dl>
        {task.body && (
          <div className="stack" style={{ gap: 4 }}>
            <span className="overline">{t('tasks.body')}</span>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{task.body}</p>
          </div>
        )}
        <div className="stack" style={{ gap: 6 }}>
          <span className="overline">{t('tasks.related')}</span>
          <div className="list-item" style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', minHeight: 0 }}>
            <Icon name="link" className="muted" />
            <EntityLink type={task.entityType} id={task.entityId} name={entityName} />
          </div>
        </div>
      </div>
      <div className="panel-f" style={{ padding: '10px 14px' }}>
        <span className="spacer" />
        <button className="btn btn-secondary btn-sm" type="button" disabled={busy} onClick={onComplete}>
          <Icon name="check" size="sm" />
          {t('tasks.complete')}
          <span className="kbd">X</span>
        </button>
      </div>
    </aside>
  )
}

/** Ссылка на запись задачи: вид и, если список виден смотрящему, номер или название. */
function EntityLink({ type, id, name }: { type?: string | null; id?: string | null; name: string | undefined }) {
  const { t } = useApp()
  // Задача без записи (#110): ключей в ответе нет вовсе — словами, а не пустой ссылкой.
  if (type == null || id == null) return <span className="t-sm muted ellipsis">{t('tasks.noRecord')}</span>
  const base = ENTITY_PATH[type]
  const label = name ? `${t(`myday.entity.${type}`)} · ${name}` : t(`myday.entity.${type}`)
  if (!base) return <span className="t-sm muted ellipsis">{label}</span>
  return (
    <Link className="t-sm ellipsis" to={`${base}/${id}`}>
      {label}
    </Link>
  )
}

/**
 * Названия связанных записей через механизм списков.
 *
 * В ответе задач только вид и идентификатор. Список сужен областью видимости, поэтому
 * запись, которую смотрящий не видит, останется подписанной видом — и не раскроется.
 */
function useEntityNames(tasks: Task[]): Map<string, string> {
  const { user } = useApp()
  const ids = (type: string) =>
    tasks.flatMap((task) => (task.entityType === type && task.entityId != null ? [task.entityId] : []))
  const deals = useRecords(can(user, 'sales.deal', 'read') ? 'sales.deal' : null, ['number', 'title'], ids('deal'))
  const parties = useRecords(can(user, 'sales.counterparty', 'read') ? 'sales.counterparty' : null, ['name'], ids('counterparty'))
  const names = new Map<string, string>()
  for (const [id, row] of deals.rows) names.set(id, [row.number, row.title].filter(Boolean).join(' · '))
  for (const [id, row] of parties.rows) if (row.name) names.set(id, String(row.name))
  return names
}

function dedupe(tasks: Task[]): Task[] {
  const seen = new Set<string>()
  return tasks.filter((task) => (seen.has(task.id) ? false : (seen.add(task.id), true)))
}

function whenOf(task: Task): string | null {
  return task.dueAt ?? task.scheduledAt ?? null
}

function byWhen(a: Task, b: Task): number {
  const left = whenOf(a)
  const right = whenOf(b)
  if (!left) return right ? 1 : 0
  if (!right) return -1
  return Date.parse(left) - Date.parse(right)
}

/** Через сколько календарных дней (по часам смотрящего) наступит момент; null — без срока. */
function dayOffset(iso: string | null, now: Date): number | null {
  if (!iso) return null
  const at = new Date(iso)
  const a = new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime()
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  return Math.round((a - b) / 86_400_000)
}

function time(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
}

function shortWhen(iso: string, now: Date, locale: Locale): string {
  if (dayOffset(iso, now) === 0) return time(iso, locale)
  const date = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(new Date(iso))
  return `${date}, ${time(iso, locale)}`
}

function longWhen(iso: string, now: Date, locale: Locale, t: (key: string) => string): string {
  const offset = dayOffset(iso, now)
  if (offset === 0) return `${t('tasks.today')}, ${time(iso, locale)}`
  if (offset === 1) return `${t('tasks.tomorrow')}, ${time(iso, locale)}`
  return shortWhen(iso, now, locale)
}
